import {
  Component,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import {
  DirectoryNumberRef,
  Global360View,
  LineButton,
  LineFeature,
  LineFeatureFieldConfig,
  Phone,
  TranslationPattern,
  VoicemailRef,
} from '../../../models/generated/ziroModels';
import { LineButtonPeripheralSummary } from '../../layout/phone-buttons-standard.component';
import {
  PhoneButtonsLineExtensionSearchComponent,
  PhoneButtonsLineExtensionSearchEntity,
} from './phone-buttons-line-extension-search/phone-buttons-line-extension-search.component';
import { Observable, Subject, Subscription } from 'rxjs';
import { isEqual } from 'lodash';
import { PhoneButtonsLineHelpdeskFormComponent } from './phone-buttons-line-helpdesk-form/phone-buttons-line-helpdesk-form.component';
import { ExtensionMobilityDnD } from '../../phone-buttons.component';
import { PhoneUiContext } from '../../contexts/phone-ui.context';

@Component({
  selector: 'ziro-phone-buttons-line-helpdesk',
  templateUrl: './phone-buttons-line-helpdesk.component.html',
  imports: [PhoneButtonsLineExtensionSearchComponent, PhoneButtonsLineHelpdeskFormComponent],
})
export class PhoneButtonsLineHelpdeskComponent implements OnInit, OnChanges, OnDestroy {
  private phoneUiContext = inject(PhoneUiContext);

  @ViewChild(PhoneButtonsLineExtensionSearchComponent) lineExtensionSearch: PhoneButtonsLineExtensionSearchComponent;
  @ViewChild(PhoneButtonsLineHelpdeskFormComponent) lineHelpdeskFormComponent: PhoneButtonsLineHelpdeskFormComponent;

  readonly lineFeature = input<LineFeature>();
  readonly dnRef = input<DirectoryNumberRef>();
  readonly fieldConfig = input<LineFeatureFieldConfig>();
  readonly phone = input<Phone | ExtensionMobilityDnD>();
  readonly siteId = input<number>();
  readonly lineButtonSummary = input<LineButtonPeripheralSummary>();
  readonly buttonIndex = input<number>();
  readonly parentSubmit$ = input<Observable<any>>();
  readonly global360View = input<Global360View>();

  readonly lineButtonWasUpdated = output<ZiroFormsUpdate<LineButton>>();
  readonly dnUpdated = output<boolean>();
  readonly lineButtonSummaryWasUpdated = output<LineButtonPeripheralSummary>();

  extensionSearchEntity = {} as PhoneButtonsLineExtensionSearchEntity;
  isLineFeaturePending = false;
  modalTranslationPattern: TranslationPattern;
  isSingleButton: boolean;

  _validateAndSubmitSource = new Subject<boolean>();

  private _formValidationStates = {
    extensionSearch: null as ZiroFormsValidationState,
    lineFeature: null as ZiroFormsValidationState,
  };
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.isSingleButton = this.phone().buttons.length === 1;
    if (this.isSingleButton) {
      const sub = this.phoneUiContext.isSaving$.pipe().subscribe((isSubmitting) => {
        // These properties are sometimes unavailable when switching models on a public phone
        if (this.lineExtensionSearch) {
          this.lineExtensionSearch.isSubmitting = isSubmitting;
        }
        if (this.lineHelpdeskFormComponent) {
          this.lineHelpdeskFormComponent.fieldComponents.forEach((key) => {
            key.applyState({
              ...key.state,
              disabled: isSubmitting,
            });
          });
        }
      });
      this._subscriptions.add(sub);
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.dnRef && changes.dnRef.currentValue !== changes.dnRef.previousValue) {
      this.extensionSearchEntity = { ...this.extensionSearchEntity, dn: changes.dnRef.currentValue };
    }
    if (
      changes.lineButtonSummary &&
      changes.lineButtonSummary.currentValue !== changes.lineButtonSummary.previousValue
    ) {
      this.extensionSearchEntity = {
        ...this.extensionSearchEntity,
        peripherals: changes.lineButtonSummary.currentValue,
      };
    }
    if (changes.lineFeature && changes.lineFeature.currentValue) {
      this.isLineFeaturePending = false;
    }
  }

  onLineFeatureUpdate($event: ZiroFormsUpdate<LineFeature>): void {
    if (!isEqual($event.new, $event.old) || !isEqual(this._formValidationStates.lineFeature, $event.valid)) {
      this._formValidationStates.lineFeature = $event.valid;

      const dnRef = this.dnRef();
      const updateNew: LineButton = {
        type: 'Line',
        lineFeature: $event.new,
        dn: dnRef ? { ...dnRef } : null,
      };
      const old = { ...updateNew, lineFeature: $event.old };
      this.lineButtonWasUpdated.emit({ new: updateNew, old: old, valid: this._validationState() });
    }
  }

  onExtensionChanged($event: ZiroFormsUpdate<PhoneButtonsLineExtensionSearchEntity>): void {
    if (!isEqual($event.new.dn, $event.old.dn) || !isEqual(this._formValidationStates.extensionSearch, $event.valid)) {
      this._formValidationStates.extensionSearch = $event.valid;

      if (!$event.new.dn?.extension) {
        // if the extension is absent, the line feature form is hidden so it'll always be valid.
        this._formValidationStates.lineFeature = ZiroFormsValidationState.VALID;
      } else if (!$event.old.dn?.extension) {
        // if the extension was just added, show the spinner on the extension search field.
        this.isLineFeaturePending = true;
      }

      const updateNew: LineButton = {
        type: 'Line',
        dn: $event.new.dn,
        lineFeature: this.lineFeature() ? { ...this.lineFeature() } : null,
      };
      const old = { ...updateNew, dn: $event.old.dn };
      this.lineButtonWasUpdated.emit({ new: updateNew, old: old, valid: this._validationState() });
    }
  }

  onVoicemailUpdate($event: VoicemailRef): void {
    this.lineButtonSummaryWasUpdated.emit({ ...this.lineButtonSummary(), voicemail: $event });
  }

  fetchLineFeature($event: boolean): void {
    this.dnUpdated.emit($event);
  }

  private _validationState(): ZiroFormsValidationState {
    const validationStates = Object.values(this._formValidationStates);

    if (validationStates.includes(ZiroFormsValidationState.INVALID)) {
      return ZiroFormsValidationState.INVALID;
    } else if (validationStates.includes(ZiroFormsValidationState.WARNING)) {
      return ZiroFormsValidationState.WARNING;
    } else if (validationStates.includes(ZiroFormsValidationState.PENDING)) {
      return ZiroFormsValidationState.PENDING;
    } else {
      return ZiroFormsValidationState.VALID;
    }
  }

  onDnCreated($event: any): void {
    if ($event) {
      this.modalTranslationPattern = $event.translationPatterns;
      const updateNew: LineButton = {
        type: 'Line',
        dn: $event.dnRef,
        lineFeature: this.lineFeature() ? { ...this.lineFeature() } : null,
      };
      const old = { ...updateNew, dn: $event.dnRef };
      this.lineButtonWasUpdated.emit({ new: updateNew, old: old, valid: this._validationState() });
    }
  }
}
